/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { API_KEYS } from '@shared/config';
import { Logger } from './logger';

interface TokenResponse {
    access_token: string;
    expires_in: number;
    scope: string;
    token_type: string;
}

let cachedToken: string | null = null;
let tokenExpiryTime: number = 0;

/**
 * Gets a valid Google Workspace Access Token.
 * Uses a refresh token to obtain a new access token if the cached one is expired or missing.
 */
export const getGWorkspaceAccessToken = async (): Promise<string> => {
    // Note: We need to ensure API_KEYS.googleWorkspace exists in Shared/config or GOOGLE_CONFIG
    // For now, aligning with what the code expect or what's in Shared/config
    const credentials = (API_KEYS as any).googleWorkspace || {};
    const { clientId, clientSecret, refreshToken } = credentials;

    // Manual check for placeholder/missing credentials
    if (!refreshToken || refreshToken === 'PLACEHOLDER_REFRESH_TOKEN' ||
        !clientId || clientId.includes('PLACEHOLDER') ||
        !clientSecret || clientSecret.includes('PLACEHOLDER')) {
        Logger.warn('Google Workspace credentials are missing or placeholders. Authentication will likely fail.');
        return refreshToken || '';
    }

    // Check if cached token is still valid (with 30-second buffer)
    const now = Date.now();
    if (cachedToken && tokenExpiryTime > now + 30000) {
        return cachedToken;
    }

    try {
        Logger.info('Refreshing Google Workspace access token...');

        const response = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                client_id: clientId,
                client_secret: clientSecret,
                refresh_token: refreshToken,
                grant_type: 'refresh_token',
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.error_description || errorData.error || response.statusText;
            throw new Error(`Failed to refresh Google token: ${errorMessage}`);
        }

        const data: TokenResponse = await response.json();

        cachedToken = data.access_token;
        // Set expiry time (data.expires_in is in seconds)
        tokenExpiryTime = now + (data.expires_in * 1000);

        Logger.info('Successfully refreshed Google Workspace access token');
        return cachedToken;
    } catch (error) {
        Logger.error('Error refreshing Google Workspace token', error);
        throw error;
    }
};

/**
 * Generates the Google OAuth 2.0 Authorization URL for obtaining an initial refresh token.
 */
export const getGWorkspaceAuthUrl = (): string => {
    const credentials = (API_KEYS as any).googleWorkspace || {};
    const { clientId } = credentials;
    const redirectUri = 'http://localhost:3000/auth/callback'; // Mock redirect
    const scopes = [
        'https://www.googleapis.com/auth/gmail.send',
        'https://www.googleapis.com/auth/calendar.events',
        'https://www.googleapis.com/auth/drive.readonly'
    ];

    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: scopes.join(' '),
        access_type: 'offline', // Required for refresh token
        prompt: 'consent'       // Force consent to ensure refresh token is provided
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

/**
 * Exchanges an authorization code for an initial set of tokens.
 */
export const exchangeCodeForTokens = async (code: string): Promise<any> => {
    const credentials = (API_KEYS as any).googleWorkspace || {};
    const { clientId, clientSecret } = credentials;
    const redirectUri = 'http://localhost:3000/auth/callback';

    try {
        Logger.info('Exchanging code for Google Workspace tokens...');

        const response = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                code,
                client_id: clientId,
                client_secret: clientSecret,
                redirect_uri: redirectUri,
                grant_type: 'authorization_code',
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to exchange code: ${errorData.error_description || errorData.error}`);
        }

        const data = await response.json();

        if (data.refresh_token) {
            Logger.info('INITIAL REFRESH TOKEN OBTAINED. Please update your config:', {
                refreshToken: data.refresh_token
            });
            console.log('--- NEW GOOGLE REFRESH TOKEN ---');
            console.log(data.refresh_token);
            console.log('-------------------------------');
        } else {
            Logger.warn('No refresh token returned. Did you include access_type=offline and prompt=consent?');
        }

        return data;
    } catch (error) {
        Logger.error('Error exchanging code for tokens', error);
        throw error;
    }
};
